import type { LoyaltyLevel } from '~~/types/loyaltyLevel/loyaltyLevel';

export const DISCOUNT_TYPE = {
  Relative: 'relative',
  Absolute: 'absolute',
  Fixed: 'fixed',
  GiftLineItem: 'giftLineItem',
  Shipping: 'shipping',
} as const;

export type DiscountType = ValueOf<typeof DISCOUNT_TYPE>

export const DISCOUNT_CATEGORY = {
  VOLUME: 'volume',
  LOYALTY: 'loyalty',
  PROMO_CODE: 'promoCode',
  DISTINCT_PRODUCTS: 'distinctProducts',
  OTHER: 'other',
} as const;

export type DiscountCategory = ValueOf<typeof DISCOUNT_CATEGORY>

export type DiscountTarget =
  | 'lineItems'
  | 'multiBuyLineItems'
  | 'customLineItems'
  | 'multiBuyCustomLineItems'
  | 'shipping'
  | null;

export interface MappedDiscount {
  id: string;
  name: string;
  type: DiscountType;
  target: DiscountTarget;
  value: number;
  code?: string;
  discountedAmount: number;
  requiresDiscountCode: boolean;
  products: {
    name: string;
    discountedAmount: number;
  }[];
  category: DiscountCategory;
  minimalQuantity?: number;
  loyaltyLevel?: {
    levelNumber: LoyaltyLevel['levelNumber'];
    spendingRange?: LoyaltyLevel['spendingRange'];
  };
}

export type VolumeDiscount = { volume: number, discountPercentage: number };

/**
 * Raw commercetools cart discount, as returned by any of the cart discount queries.
 * Shared by every discount category - the category specific parts (percentage,
 * minimal quantity, ...) are read from it with the `~~/helpers/discounts` getters.
 */
export type CartDiscountRaw = {
  key: string,
  id: string,
  isActive: boolean,
  cartPredicate?: string,
  value?: {
    type: string,
    permyriad?: number
  },
  target?: {
    type: string,
    predicate?: string
  },
  custom?: {
    customFieldsRaw?: {
      name: string,
      value: string | number
    }[]
  }
}

export type VolumeDiscountConfigurations = Record<string, VolumeDiscount[]>;

export type DiscountData = {
  cartDiscounts: {
    results: CartDiscountRaw[]
  }
}

export type DistinctProductsDiscount = { minimalQuantity: number, discountPercentage: number };
